import { useEffect, useState } from "react";
import { Target, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const PERIOD_LABEL = { day: "Daily", week: "Weekly", month: "Monthly" };
const MODE_LABEL = { any: "Any mode", easy: "Easy only", hardcore: "Hardcore only" };

export function ChallengeCard({ clan, onUpdate }) {
  const ch = clan.challenge;
  const [editing, setEditing] = useState(false);
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({ category: ch?.category || "paramedic", mode: ch?.mode || "any", period: ch?.period || "week" });

  useEffect(() => {
    if (editing && !meta) api.get("/clans/categories").then((r) => setMeta(r.data));
  }, [editing, meta]);

  const save = async () => {
    try {
      const { data } = await api.post("/clans/mine/challenge", form);
      onUpdate(data);
      setEditing(false);
      toast.success("Challenge set for the clan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set challenge");
    }
  };

  const clear = async () => {
    const { data } = await api.delete("/clans/mine/challenge");
    onUpdate(data);
    toast.success("Challenge cleared");
  };

  return (
    <div className="panel p-6" data-testid="challenge-card">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] tracking-[0.3em] text-muted-foreground">// CLAN CHALLENGE</div>
        {clan.is_owner && !editing && (
          <div className="flex gap-2">
            <button onClick={() => setEditing(true)} data-testid="challenge-edit-btn" className="border border-phosphor/50 px-3 py-1.5 text-[10px] tracking-widest hover:bg-primary/10">
              {ch ? "CHANGE" : "SET CHALLENGE"}
            </button>
            {ch && (
              <button onClick={clear} data-testid="challenge-clear-btn" className="border border-destructive/50 text-destructive px-3 py-1.5 text-[10px] tracking-widest hover:bg-destructive/10">
                <Trash2 className="w-3 h-3 inline" />
              </button>
            )}
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-3" data-testid="challenge-editor">
          <label className="block">
            <span className="text-[10px] tracking-widest text-muted-foreground">CATEGORY</span>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="challenge-category"
              className="mt-1 w-full bg-background border border-phosphor/30 focus:border-phosphor outline-none px-3 py-2 text-sm">
              {(meta?.categories || []).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] tracking-widest text-muted-foreground">MODE</span>
              <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="challenge-mode"
                className="mt-1 w-full bg-background border border-phosphor/30 outline-none px-3 py-2 text-sm">
                {Object.entries(MODE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] tracking-widest text-muted-foreground">PERIOD</span>
              <select value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="challenge-period"
                className="mt-1 w-full bg-background border border-phosphor/30 outline-none px-3 py-2 text-sm">
                {Object.entries(PERIOD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={save} data-testid="challenge-save" className="bg-primary text-primary-foreground px-5 py-2 text-[10px] tracking-widest font-semibold hover:bg-primary/90">SAVE</button>
            <button onClick={() => setEditing(false)} data-testid="challenge-cancel" className="border border-border px-5 py-2 text-[10px] tracking-widest">CANCEL</button>
          </div>
        </div>
      ) : ch ? (
        <div data-testid="challenge-active">
          <div className="flex items-center gap-3">
            <Target className="w-6 h-6 text-phosphor" />
            <div>
              <div className="text-xl font-extrabold">{ch.label}</div>
              <div className="text-[10px] tracking-widest text-muted-foreground">
                {PERIOD_LABEL[ch.period].toUpperCase()} · {MODE_LABEL[ch.mode].toUpperCase()} · {ch.window_start} → {ch.window_end}
              </div>
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Each member's best single score in this window counts. Clan score = average of the top 5. The challenge renews automatically every {ch.period}.
          </p>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground" data-testid="challenge-empty">
          {clan.is_owner ? "No challenge yet — pick a category and period so your clan has something to chase." : "The clan owner hasn't set a challenge yet."}
        </div>
      )}
    </div>
  );
}
